import { motion } from 'framer-motion';
import { FileText, ArrowLeft, Compass } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Decorative top border */}
      <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400" />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-xl blur-md opacity-30" />
                <div className="relative w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Compass className="w-8 h-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-display font-bold text-blue-900 tracking-tight">
                  Masonic Tools
                </h1>
                <p className="text-sm text-gray-500 font-serif italic">Professional Unit Management</p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white font-semibold rounded-xl hover:bg-blue-800 transition-all duration-300 hover:shadow-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white px-4 sm:px-6 lg:px-8 py-16 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-blue-900 rounded-full blur-xl opacity-20" />
              <div className="relative p-6 bg-blue-900 rounded-full shadow-2xl">
                <FileText className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-display font-bold mb-6 text-blue-900 leading-tight tracking-tight">
              Terms and Conditions
            </h2>
            <p className="text-xl text-gray-600 mb-4 font-serif">
              Please read these terms carefully before using our applications.
            </p>
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <div className="w-12 h-px bg-gray-300" />
              <Compass className="w-4 h-4" />
              <div className="w-12 h-px bg-gray-300" />
            </div>
            <p className="text-sm text-gray-500 mt-4">Last Updated: October 9, 2025</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-white px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed mb-0">
                These Terms and Conditions govern your use of all Masonic Tools applications, including Dining, Secretary, Treasurer, and any future applications within the Masonic Tools suite.
              </p>
            </div>

            <div className="space-y-12">
              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">1.</span> Acceptance of Terms
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  By accessing or using any Masonic Tools application ("Services"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not access or use our Services. Your continued use of the Services constitutes acceptance of any modifications to these Terms.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">2.</span> Description of Services
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Masonic Tools provides a suite of web-based applications designed for Masonic unit management, including:
                </p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li><strong>Dining:</strong> Event and dining management system with RSVP tracking, seating arrangements, and payment processing</li>
                  <li><strong>Secretary:</strong> Administrative tools for member records, meeting minutes, and document management</li>
                  <li><strong>Treasurer:</strong> Financial management tools for budgeting, reporting, and subscription management</li>
                  <li>Additional applications and features as they become available</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We reserve the right to modify, suspend, or discontinue any aspect of the Services at any time without prior notice.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">3.</span> User Accounts and Registration
                </h3>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">3.1 Account Creation</h4>
                    <p className="text-gray-700 leading-relaxed">
                      To use our Services, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">3.2 Eligibility</h4>
                    <p className="text-gray-700 leading-relaxed">
                      You must be at least 18 years old and a member of a recognized Masonic unit to use our Services. By creating an account, you represent that you meet these eligibility requirements.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">3.3 Account Security</h4>
                    <p className="text-gray-700 leading-relaxed">
                      You must immediately notify us of any unauthorized use of your account or any other breach of security. We recommend enabling two-factor authentication for enhanced security. You are responsible for any actions taken using your account credentials.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">3.4 Account Termination</h4>
                    <p className="text-gray-700 leading-relaxed">
                      We reserve the right to suspend or terminate your account at any time for violation of these Terms, fraudulent activity, or any other reason at our sole discretion. You may also delete your account at any time through the application settings.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">4.</span> User Responsibilities and Conduct
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">When using our Services, you agree to:</p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>Provide accurate and truthful information</li>
                  <li>Use the Services only for lawful purposes and in accordance with these Terms</li>
                  <li>Respect the privacy and rights of other users</li>
                  <li>Not attempt to gain unauthorized access to any part of the Services</li>
                  <li>Not interfere with or disrupt the Services or servers</li>
                  <li>Not use the Services to transmit viruses, malware, or harmful code</li>
                  <li>Not engage in any activity that could damage, disable, or impair the Services</li>
                  <li>Not use automated systems or software to extract data from the Services</li>
                  <li>Not impersonate any person or entity or misrepresent your affiliation</li>
                  <li>Comply with all applicable local, state, national, and international laws</li>
                </ul>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">5.</span> Payment Terms
                </h3>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">5.1 Fees and Subscriptions</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Certain features of our Services may require payment of fees. By subscribing to a paid plan or purchasing services, you agree to pay all applicable fees as described at the time of purchase. All fees are non-refundable unless otherwise stated or required by law.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">5.2 Event Payments</h4>
                    <p className="text-gray-700 leading-relaxed">
                      When you register for unit events through our Dining application, you agree to pay the stated event fees. Payment processing is handled securely through Stripe, and you agree to comply with Stripe's terms of service.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">5.3 Automatic Renewal</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Subscription fees are billed in advance on a recurring basis (monthly or annually, as selected). Your subscription will automatically renew at the end of each billing period unless you cancel before the renewal date. You can cancel your subscription at any time through your account settings.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">5.4 Price Changes</h4>
                    <p className="text-gray-700 leading-relaxed">
                      We reserve the right to modify our pricing at any time. We will provide reasonable notice of any price changes. Your continued use of the Services after price changes take effect constitutes acceptance of the new prices.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">5.5 Refunds</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Refunds for event registrations may be available according to the specific event's cancellation policy set by your unit. Subscription fees are generally non-refundable, but we may consider refund requests on a case-by-case basis.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">6.</span> Intellectual Property Rights
                </h3>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">6.1 Our Content</h4>
                    <p className="text-gray-700 leading-relaxed">
                      The Services, including all content, features, functionality, designs, logos, and trademarks, are owned by Masonic Tools and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our Services without our prior written permission.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">6.2 Your Content</h4>
                    <p className="text-gray-700 leading-relaxed">
                      You retain ownership of any content you submit, upload, or create through the Services ("User Content"). By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display your User Content solely for the purpose of providing and improving the Services.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">6.3 Content Responsibility</h4>
                    <p className="text-gray-700 leading-relaxed">
                      You are solely responsible for your User Content and represent that you have all necessary rights to grant the license described above. We reserve the right to remove any User Content that violates these Terms or is otherwise objectionable.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">7.</span> Privacy and Data Protection
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Your use of our Services is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information. By using our Services, you consent to our collection and use of your information as described in the Privacy Policy. Please review our <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link> for more information.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">8.</span> Third-Party Services and Links
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Our Services may integrate with or contain links to third-party services, including:
                </p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>Stripe for payment processing</li>
                  <li>Google Firebase for authentication and data storage</li>
                  <li>Other third-party tools and services</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We are not responsible for the content, privacy policies, or practices of any third-party services. Your use of third-party services is at your own risk and subject to their respective terms and conditions.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">9.</span> Disclaimers and Limitations of Liability
                </h3>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">9.1 Service Availability</h4>
                    <p className="text-gray-700 leading-relaxed">
                      The Services are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Services will be uninterrupted, secure, or error-free. We reserve the right to modify, suspend, or discontinue the Services at any time.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">9.2 No Warranties</h4>
                    <p className="text-gray-700 leading-relaxed">
                      We make no warranties regarding the accuracy, reliability, or completeness of any content or information provided through the Services. We disclaim all warranties, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">9.3 Limitation of Liability</h4>
                    <p className="text-gray-700 leading-relaxed">
                      To the fullest extent permitted by law, Masonic Tools and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising out of or related to your use of the Services, even if we have been advised of the possibility of such damages. In no event shall our total liability exceed the amount paid by you to us in the twelve months preceding the claim.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">9.4 User Responsibility</h4>
                    <p className="text-gray-700 leading-relaxed">
                      You acknowledge that you are solely responsible for your use of the Services and any decisions made based on information obtained through the Services. We are not responsible for any errors, omissions, or inaccuracies in User Content or for any actions taken in reliance on such content.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">10.</span> Indemnification
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Masonic Tools and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Services; (b) your violation of these Terms; (c) your violation of any rights of another party; or (d) your User Content.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">11.</span> Data Backup and Loss
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  While we implement regular backups and security measures to protect your data, we are not responsible for any loss of data or content. You are responsible for maintaining your own backup copies of any important information or content you upload to the Services. We recommend regularly exporting and backing up your data.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">12.</span> Dispute Resolution and Governing Law
                </h3>
                <div className="ml-8 space-y-4">
                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">12.1 Governing Law</h4>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">12.2 Dispute Resolution</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Any disputes arising out of or related to these Terms or the Services shall be resolved through good faith negotiations. If the dispute cannot be resolved through negotiation, it shall be submitted to binding arbitration in accordance with the rules of the appropriate arbitration body in the United Kingdom.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-blue-800 mb-2">12.3 Jurisdiction</h4>
                    <p className="text-gray-700 leading-relaxed">
                      You agree to submit to the personal jurisdiction of the courts located in England and Wales for the purpose of litigating all such claims or disputes.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">13.</span> Changes to Terms
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on this page and updating the "Last Updated" date. We may also provide notice through email or the Services. Your continued use of the Services after changes become effective constitutes acceptance of the modified Terms. If you do not agree to the modified Terms, you must stop using the Services.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">14.</span> Termination
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We may terminate or suspend your access to the Services immediately, without prior notice or liability, for any reason, including but not limited to:
                </p>
                <ul className="list-disc ml-6 text-gray-700 space-y-2">
                  <li>Breach of these Terms</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Request by law enforcement or government agencies</li>
                  <li>Extended periods of inactivity</li>
                  <li>Technical or security issues</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Upon termination, your right to use the Services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">15.</span> Severability
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect and enforceable.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">16.</span> Entire Agreement
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  These Terms, together with our Privacy Policy and any other legal notices published by us on the Services, constitute the entire agreement between you and Masonic Tools concerning the Services and supersede all prior or contemporaneous communications and proposals, whether oral or written.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">17.</span> Waiver
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term. Our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">18.</span> Force Majeure
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, network infrastructure failures, strikes, or shortages of transportation, facilities, fuel, energy, labor, or materials.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">19.</span> Assignment
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  You may not assign or transfer these Terms or your rights and obligations under these Terms without our prior written consent. We may assign these Terms at any time without notice to you. Any attempted transfer or assignment in violation of this section will be null and void.
                </p>
              </div>

              <div>
                <h3 className="text-3xl font-display font-bold text-blue-900 mb-4 flex items-center gap-3">
                  <span className="text-yellow-600">20.</span> Contact Information
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions, concerns, or feedback regarding these Terms or the Services, please contact us:
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <p className="text-gray-700 leading-relaxed mb-2">
                    <strong className="text-blue-900">Masonic Tools</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Email: <a href="mailto:support@masonic-tools.co.uk" className="text-blue-600 hover:text-blue-800 underline">support@masonic-tools.co.uk</a>
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Legal: <a href="mailto:legal@masonic-tools.co.uk" className="text-blue-600 hover:text-blue-800 underline">legal@masonic-tools.co.uk</a>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Website: <a href="https://masonic-tools.co.uk" className="text-blue-600 hover:text-blue-800 underline">https://masonic-tools.co.uk</a>
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-900 p-6 rounded-r-lg mt-8">
                <p className="text-gray-700 leading-relaxed mb-0">
                  <strong className="text-blue-900">Acknowledgment:</strong> By using Masonic Tools applications, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these Terms, you must not access or use our Services.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-t border-blue-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 rounded-xl blur-md opacity-30" />
                <div className="relative w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Compass className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <p className="text-white font-display font-bold text-xl">Masonic Tools</p>
                <p className="text-sm text-blue-200 font-serif italic">Professional Unit Management</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-blue-100 font-medium">© {new Date().getFullYear()} Masonic Tools. All rights reserved.</p>
              <div className="flex gap-4 mt-2 justify-center md:justify-end">
                <Link to="/privacy" className="text-sm text-blue-300 hover:text-yellow-400 transition-colors">Privacy Policy</Link>
                <span className="text-blue-300">•</span>
                <Link to="/terms" className="text-sm text-blue-300 hover:text-yellow-400 transition-colors">Terms & Conditions</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;

